const cardHoverDisabledAttribute = 'data-iicase-disable-card-hover';
const sharedProductCardHoverPatchedFlag = '__iicaseSharedProductCardHoverPatched';
const sharedProductCardInstancePatchedFlag = '__iicaseSharedProductCardInstancePatched';

function lockSharedProductCardAfterSwatchSelection(target) {
  if (!(target instanceof HTMLInputElement) || !target.checked) return;
  if (!target.closest('swatches-variant-picker-component')) return;

  target.closest('product-card')?.setAttribute(cardHoverDisabledAttribute, 'true');
}

function patchSharedProductCardInstance(productCard) {
  if (productCard[sharedProductCardInstancePatchedFlag]) return;

  const resetVariant = productCard.resetVariant;
  if (typeof resetVariant === 'function') {
    const patchedResetVariant = function iicaseSharedResetVariant(...args) {
      if (this.hasAttribute?.(cardHoverDisabledAttribute)) return;
      return resetVariant.apply(this, args);
    };

    if (typeof resetVariant.cancel === 'function') {
      patchedResetVariant.cancel = resetVariant.cancel.bind(resetVariant);
    }

    productCard.resetVariant = patchedResetVariant;
  }

  Object.defineProperty(productCard, sharedProductCardInstancePatchedFlag, {
    value: true,
    configurable: true,
  });
}

function patchSharedProductCardHover() {
  customElements
    .whenDefined('product-card')
    .then(() => {
      const ProductCard = customElements.get('product-card');
      const prototype = ProductCard?.prototype;
      if (!prototype || prototype[sharedProductCardHoverPatchedFlag]) return;

      const connectedCallback = prototype.connectedCallback;
      const previewImage = prototype.previewImage;
      const resetImage = prototype.resetImage;
      const previewVariant = prototype.previewVariant;

      if (typeof connectedCallback === 'function') {
        prototype.connectedCallback = function iicaseSharedProductCardConnectedCallback(...args) {
          const result = connectedCallback.apply(this, args);
          patchSharedProductCardInstance(this);
          return result;
        };
      }

      if (typeof previewImage === 'function') {
        prototype.previewImage = function iicaseSharedPreviewImage(event) {
          if (this.hasAttribute?.(cardHoverDisabledAttribute)) return;
          return previewImage.call(this, event);
        };
      }

      if (typeof resetImage === 'function') {
        prototype.resetImage = function iicaseSharedResetImage(event) {
          if (this.hasAttribute?.(cardHoverDisabledAttribute)) return;
          return resetImage.call(this, event);
        };
      }

      if (typeof previewVariant === 'function') {
        prototype.previewVariant = function iicaseSharedPreviewVariant(id, event) {
          const result = previewVariant.call(this, id, event);
          const target = event?.target instanceof Element ? event.target.closest('label')?.querySelector('input') : null;
          lockSharedProductCardAfterSwatchSelection(target);
          return result;
        };
      }

      Object.defineProperty(prototype, sharedProductCardHoverPatchedFlag, {
        value: true,
        configurable: true,
      });

      document.querySelectorAll('product-card').forEach(patchSharedProductCardInstance);
    })
    .catch(() => {});
}

document.addEventListener('change', (event) => {
  lockSharedProductCardAfterSwatchSelection(event.target);
}, true);

patchSharedProductCardHover();

const iicaseMarqueeOverflowClass = 'iicase-marquee-overflow';
const iicaseMarqueeOverflowSelector =
  '.iicase-product-card-title, .iicase-product-card-feature-strip, .iicase-pdp-feature-strip';
let iicaseMarqueeOverflowResizeObserver = null;
let iicaseMarqueeOverflowMutationObserver = null;

function updateIicaseMarqueeOverflow(element) {
  const track = element.firstElementChild;
  if (!(track instanceof HTMLElement)) return;

  const elementStyles = window.getComputedStyle(element);
  const inlineStartPadding =
    Number.parseFloat(elementStyles.paddingInlineStart || elementStyles.paddingLeft) || 0;
  const inlineEndPadding =
    Number.parseFloat(elementStyles.paddingInlineEnd || elementStyles.paddingRight) || 0;
  const elementPadding =
    inlineStartPadding +
    inlineEndPadding;
  const availableWidth = Math.max(0, element.clientWidth - elementPadding);
  const previousStyles = {
    display: track.style.display,
    maxWidth: track.style.maxWidth,
    overflow: track.style.overflow,
    textOverflow: track.style.textOverflow,
    width: track.style.width,
  };

  track.style.display = 'inline-block';
  track.style.width = 'max-content';
  track.style.maxWidth = 'none';
  track.style.overflow = 'visible';
  track.style.textOverflow = 'clip';

  const trackWidth = Math.max(track.getBoundingClientRect().width, track.scrollWidth);

  track.style.display = previousStyles.display;
  track.style.width = previousStyles.width;
  track.style.maxWidth = previousStyles.maxWidth;
  track.style.overflow = previousStyles.overflow;
  track.style.textOverflow = previousStyles.textOverflow;

  const overflowDistance = Math.ceil(trackWidth - availableWidth);
  const hasOverflow = overflowDistance > 1;

  element.classList.toggle(iicaseMarqueeOverflowClass, hasOverflow);

  if (hasOverflow) {
    const duration = Math.min(18, Math.max(7, overflowDistance / 12));
    element.style.setProperty('--iicase-marquee-duration', `${duration.toFixed(2)}s`);
    element.style.setProperty('--iicase-marquee-distance', `${-overflowDistance}px`);
  } else {
    element.style.removeProperty('--iicase-marquee-duration');
    element.style.removeProperty('--iicase-marquee-distance');
  }
}

function observeIicaseMarqueeOverflows(root = document) {
  const elements = root instanceof Element && root.matches(iicaseMarqueeOverflowSelector)
    ? [root]
    : Array.from(root.querySelectorAll?.(iicaseMarqueeOverflowSelector) || []);

  elements.forEach((element) => {
    updateIicaseMarqueeOverflow(element);
    iicaseMarqueeOverflowResizeObserver?.observe(element);
  });
}

function startIicaseMarqueeOverflowObserver() {
  if ('ResizeObserver' in window && !iicaseMarqueeOverflowResizeObserver) {
    iicaseMarqueeOverflowResizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => updateIicaseMarqueeOverflow(entry.target));
    });
  }

  observeIicaseMarqueeOverflows();

  if (!iicaseMarqueeOverflowMutationObserver) {
    iicaseMarqueeOverflowMutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) observeIicaseMarqueeOverflows(node);
        });
      });
    });

    iicaseMarqueeOverflowMutationObserver.observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startIicaseMarqueeOverflowObserver, { once: true });
} else {
  startIicaseMarqueeOverflowObserver();
}

const productCardFeatureStripMarqueeActiveClass = 'iicase-product-card-marquee-active';
const productCardFeatureStripMarqueeMediaQuery = window.matchMedia('(max-width: 749px)');
const productCardFeatureStripReducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
let productCardFeatureStripMarqueeObserver = null;
let productCardFeatureStripMarqueeMutationObserver = null;

function clearProductCardFeatureStripMarqueeState() {
  document
    .querySelectorAll(`product-card.${productCardFeatureStripMarqueeActiveClass}`)
    .forEach((productCard) => productCard.classList.remove(productCardFeatureStripMarqueeActiveClass));
}

function observeProductCardFeatureStripMarquees(root = document) {
  if (!productCardFeatureStripMarqueeObserver) return;

  const productCards = root instanceof Element && root.matches('product-card')
    ? [root]
    : Array.from(root.querySelectorAll?.('product-card') || []);

  productCards
    .filter((productCard) => productCard.querySelector('.iicase-product-card-feature-strip'))
    .forEach((productCard) => productCardFeatureStripMarqueeObserver.observe(productCard));
}

function startProductCardFeatureStripMarqueeObserver() {
  if (productCardFeatureStripMarqueeObserver || !('IntersectionObserver' in window)) return;

  productCardFeatureStripMarqueeObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle(productCardFeatureStripMarqueeActiveClass, entry.isIntersecting);
      });
    },
    {
      rootMargin: '0px 0px -50% 0px',
      threshold: 0,
    }
  );

  observeProductCardFeatureStripMarquees();

  productCardFeatureStripMarqueeMutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) observeProductCardFeatureStripMarquees(node);
      });
    });
  });

  productCardFeatureStripMarqueeMutationObserver.observe(document.body, { childList: true, subtree: true });
}

function stopProductCardFeatureStripMarqueeObserver() {
  productCardFeatureStripMarqueeObserver?.disconnect();
  productCardFeatureStripMarqueeMutationObserver?.disconnect();
  productCardFeatureStripMarqueeObserver = null;
  productCardFeatureStripMarqueeMutationObserver = null;
  clearProductCardFeatureStripMarqueeState();
}

function updateProductCardFeatureStripMarqueeObserver() {
  if (
    productCardFeatureStripMarqueeMediaQuery.matches &&
    !productCardFeatureStripReducedMotionMediaQuery.matches
  ) {
    startProductCardFeatureStripMarqueeObserver();
    return;
  }

  stopProductCardFeatureStripMarqueeObserver();
}

function bindIicaseMediaQueryChange(mediaQuery, callback) {
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', callback);
    return;
  }

  mediaQuery.addListener(callback);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateProductCardFeatureStripMarqueeObserver, { once: true });
} else {
  updateProductCardFeatureStripMarqueeObserver();
}

bindIicaseMediaQueryChange(productCardFeatureStripMarqueeMediaQuery, updateProductCardFeatureStripMarqueeObserver);
bindIicaseMediaQueryChange(
  productCardFeatureStripReducedMotionMediaQuery,
  updateProductCardFeatureStripMarqueeObserver
);

const productCardTitleTooltipSelector = '[data-iicase-product-card-title-tooltip]';
let productCardTitleTooltipElement = null;
let activeProductCardTitleTooltipTarget = null;

function getProductCardTitleTooltipElement() {
  if (productCardTitleTooltipElement?.isConnected) return productCardTitleTooltipElement;
  if (!document.body) return null;

  const tooltip = document.createElement('div');
  tooltip.className = 'iicase-product-card-title-tooltip';
  tooltip.setAttribute('role', 'tooltip');
  tooltip.setAttribute('aria-hidden', 'true');
  document.body.append(tooltip);
  productCardTitleTooltipElement = tooltip;

  return tooltip;
}

function positionProductCardTitleTooltip(event) {
  const tooltip = getProductCardTitleTooltipElement();
  if (!tooltip) return;

  const offset = 14;
  const margin = 8;
  const rect = tooltip.getBoundingClientRect();
  const maxX = Math.max(margin, window.innerWidth - rect.width - margin);
  const maxY = Math.max(margin, window.innerHeight - rect.height - margin);
  let x = event.clientX + offset;
  let y = event.clientY + offset;

  if (x > maxX) x = event.clientX - rect.width - offset;
  if (y > maxY) y = event.clientY - rect.height - offset;

  tooltip.style.setProperty('--iicase-tooltip-x', `${Math.max(margin, Math.min(x, maxX))}px`);
  tooltip.style.setProperty('--iicase-tooltip-y', `${Math.max(margin, Math.min(y, maxY))}px`);
}

function showProductCardTitleTooltip(target, event) {
  const text = target.getAttribute('data-iicase-product-card-title-tooltip')?.trim();
  const tooltip = getProductCardTitleTooltipElement();
  if (!text || !tooltip) return;

  activeProductCardTitleTooltipTarget = target;
  tooltip.textContent = text;
  positionProductCardTitleTooltip(event);
  tooltip.dataset.visible = 'true';
  tooltip.setAttribute('aria-hidden', 'false');
}

function hideProductCardTitleTooltip() {
  if (!productCardTitleTooltipElement) return;

  productCardTitleTooltipElement.removeAttribute('data-visible');
  productCardTitleTooltipElement.setAttribute('aria-hidden', 'true');
  activeProductCardTitleTooltipTarget = null;
}

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse') return;

  const target = event.target instanceof Element
    ? event.target.closest(productCardTitleTooltipSelector)
    : null;

  if (!(target instanceof HTMLElement) || !target.closest('product-card')) return;
  if (target === activeProductCardTitleTooltipTarget) return;

  showProductCardTitleTooltip(target, event);
});

document.addEventListener('pointermove', (event) => {
  if (event.pointerType !== 'mouse' || !activeProductCardTitleTooltipTarget) return;

  positionProductCardTitleTooltip(event);
});

document.addEventListener('pointerout', (event) => {
  if (!activeProductCardTitleTooltipTarget) return;

  const relatedTarget = event.relatedTarget;
  if (relatedTarget instanceof Node && activeProductCardTitleTooltipTarget.contains(relatedTarget)) return;

  hideProductCardTitleTooltip();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') hideProductCardTitleTooltip();
});

window.addEventListener('blur', hideProductCardTitleTooltip);
